import { createLogger } from "../utils/logger.js";
import { BlobReader, BlobWriter, ZipWriter } from "@zip.js/zip.js";
//#region ../src/storage/zip-export.ts
/**
* ZIP Export Module
*
* Exports OPFS session data as ZIP files for download.
* Uses @zip.js/zip.js library for streaming ZIP creation and reading.
*
* The ZIP uses "store" mode (no compression) because:
* 1. Images are already compressed (JPEG)
* 2. Faster packaging
* 3. Simpler implementation
*
* @zip.js/zip.js was chosen over fflate because:
* - Supports streaming reads and writes (needed for periodic sync to external file)
* - Can append to existing ZIP files via prependZip
* - Actively maintained with good TypeScript support
* - Built-in Web Worker support for non-blocking compression
*/
const log = createLogger("ZipExport");
/**
* Get the OPFS scenarios directory handle.
* Re-acquires from navigator.storage to avoid holding stale references.
* Creates on demand so it works even when initOpfsStorage only creates sessions/.
*/
async function getScenariosHandle() {
	return (await (await navigator.storage.getDirectory()).getDirectoryHandle("gps-plus-slam")).getDirectoryHandle("scenarios", { create: true });
}
/**
* Get the OPFS sessions directory handle (flat layout).
*/
async function getSessionsHandle() {
	return (await (await navigator.storage.getDirectory()).getDirectoryHandle("gps-plus-slam")).getDirectoryHandle("sessions");
}
/**
* Stream all files from a directory recursively into a ZipWriter.
* Files are read one at a time and immediately added to the ZIP,
* keeping memory usage proportional to a single file (not the total).
*
* @returns Number of files added
*/
async function streamDirectoryToZip(dirHandle, zipWriter, basePath = "") {
	let count = 0;
	for await (const entry of dirHandle.values()) {
		const relativePath = basePath ? `${basePath}/${entry.name}` : entry.name;
		if (entry.kind === "file") {
			const file = await (await dirHandle.getFileHandle(entry.name)).getFile();
			await zipWriter.add(relativePath, new BlobReader(file));
			count++;
		} else if (entry.kind === "directory") {
			const subDirHandle = await dirHandle.getDirectoryHandle(entry.name);
			count += await streamDirectoryToZip(subDirHandle, zipWriter, relativePath);
		}
	}
	return count;
}
/**
* Export a session from OPFS as a ZIP blob.
*
* The ZIP structure mirrors the OPFS session structure:
* - session.json (at root)
* - actions/000001.json, actions/000002.json, ...
* - frames/frame-000001.jpg, frames/frame-000002.jpg, ...
* - refPoints/{h3}.json (only observations from this session, scenario layout only)
*
* Uses "store" mode (compression level 0) for fast packaging.
*
* Supports both flat and scenario-based layouts:
* - `exportSessionAsZip(sessionName)` — flat layout (sessions/)
* - `exportSessionAsZip(scenarioName, sessionName)` — scenario layout (scenarios/{name}/)
*
* @returns ZIP export result with blob and file count
* @throws Error if session not found
*/
async function exportSessionAsZip(scenarioNameOrSessionName, sessionName, options) {
	let sessionHandle;
	if (sessionName) {
		const scenarioName = scenarioNameOrSessionName;
		log.info(`Exporting session: ${scenarioName}/${sessionName}`);
		const scenariosDir = await getScenariosHandle();
		let scenarioHandle;
		try {
			scenarioHandle = await scenariosDir.getDirectoryHandle(scenarioName);
		} catch {
			throw new Error(`Scenario "${scenarioName}" not found in OPFS storage`);
		}
		try {
			sessionHandle = await scenarioHandle.getDirectoryHandle(sessionName);
		} catch {
			throw new Error(`Session "${sessionName}" not found in scenario "${scenarioName}"`);
		}
	} else {
		log.info(`Exporting session: ${scenarioNameOrSessionName}`);
		const sessionsDir = await getSessionsHandle();
		try {
			sessionHandle = await sessionsDir.getDirectoryHandle(scenarioNameOrSessionName);
		} catch {
			throw new Error(`Session "${scenarioNameOrSessionName}" not found in OPFS storage`);
		}
	}
	const zipWriter = new ZipWriter(new BlobWriter("application/zip"), { level: 0 });
	let fileCount = await streamDirectoryToZip(sessionHandle, zipWriter);
	const contributors = options?.contributors ?? [];
	const seenSubdirs = /* @__PURE__ */ new Set();
	for (const contributor of contributors) {
		const subdir = contributor.subdir;
		if (!subdir || subdir.includes("/") || subdir.startsWith(".")) throw new Error(`ZipExportContributor.subdir must be a non-empty single path segment, got: ${JSON.stringify(subdir)}`);
		if (seenSubdirs.has(subdir)) throw new Error(`Duplicate ZipExportContributor.subdir registered: ${subdir}`);
		seenSubdirs.add(subdir);
		const addFile = async (relativePath, blob) => {
			if (relativePath.startsWith("/")) throw new Error(`ZipExportContributor relative path must not start with '/' (got ${relativePath})`);
			if (relativePath.includes("\\")) throw new Error(`ZipExportContributor relative path must not contain '\\' (got ${relativePath})`);
			if (relativePath.split("/").some((s) => s === ".." || s === ".")) throw new Error(`ZipExportContributor relative path must not contain '.' or '..' segments (got ${relativePath})`);
			await zipWriter.add(`${subdir}/${relativePath}`, new BlobReader(blob));
		};
		fileCount += await contributor.contribute(addFile);
	}
	const blob = await zipWriter.close();
	log.info(`ZIP created: ${blob.size} bytes, ${fileCount} files`);
	return {
		blob,
		fileCount
	};
}
/**
* Sync current OPFS session data to an external file handle.
*
* This is used for periodic sync during recording to save data to the user's
* chosen location (obtained via showSaveFilePicker). The function:
* 1. Reads all current session data from OPFS
* 2. Creates a ZIP in memory
* 3. Writes the ZIP to the external file handle
*
* This provides crash safety combined with OPFS - even if the app crashes,
* the last synced ZIP contains all data up to that point.
*
* @param fileHandle - File handle from showSaveFilePicker
* @param scenarioName - Name of the scenario
* @param sessionName - Name of the session folder
* @returns ZIP export result with blob and file count
* @throws Error if scenario or session not found
*/
async function syncToExternalZip(fileHandle, scenarioName, sessionName, options) {
	log.info(`Syncing to external ZIP: ${scenarioName}/${sessionName}`);
	const result = await exportSessionAsZip(scenarioName, sessionName, options);
	const writable = await fileHandle.createWritable();
	await writable.write(result.blob);
	await writable.close();
	log.info(`Synced ${result.blob.size} bytes to external file`);
	return result;
}
/**
* Trigger a file download in the browser.
*
* Uses showSaveFilePicker if available (for better UX on desktop),
* falls back to <a download> for broader compatibility.
*
* @param blob - The blob to download
* @param filename - Suggested filename
*/
async function downloadZip(blob, filename) {
	if ("showSaveFilePicker" in window && window.showSaveFilePicker) try {
		const writable = await (await window.showSaveFilePicker({
			suggestedName: filename,
			types: [{
				description: "ZIP Archive",
				accept: { "application/zip": [".zip"] }
			}]
		})).createWritable();
		await writable.write(blob);
		await writable.close();
		log.info(`Saved via File System Access API: ${filename}`);
		return;
	} catch (err) {
		const error = err;
		if (error.name === "AbortError") {
			log.info("User cancelled save dialog");
			return;
		}
		log.warn("showSaveFilePicker failed, using fallback:", error.message);
	}
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.style.display = "none";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	setTimeout(() => URL.revokeObjectURL(url), 1e3);
	log.info(`Download triggered via <a download>: ${filename}`);
}
/**
* Export and download a session in one step.
*
* Convenience function combining exportSessionAsZip and downloadZip.
*
* @param scenarioName - Name of the scenario
* @param sessionName - Name of the session folder
*/
async function exportAndDownloadSession(scenarioName, sessionName) {
	const { blob } = await exportSessionAsZip(scenarioName, sessionName);
	await downloadZip(blob, `${scenarioName}-${sessionName}.zip`);
}
//#endregion
export { downloadZip, exportAndDownloadSession, exportSessionAsZip, syncToExternalZip };
